import { useMemo, useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { useSubjectChapter } from '@/context/SubjectChapterContext';
import { BookOpen, AlertCircle, BookText, ChevronLeft, ChevronDown } from 'lucide-react';
import BudleeAIIcon from './BudleeAIIcon';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import LearningNode from '@/components/LearningNode';
import LessonModal from '@/components/LessonModal';
import TextbookViewer from '@/components/TextbookViewer';
import CardJourneyView from '@/components/CardJourneyView';
import BasicConceptsModal from '@/components/basicconcepts/BasicConceptsModal';
import { useLearningPack } from '@/hooks/useLearningPack';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { subjectsAPI, chaptersAPI } from '@/api/curriculum';
import { getUserData, getPortalType, getAuthToken, setAuthTokens } from '@/utils/auth';
import { trackContentEvent } from '@/analytics/analytics';
import { apiUrl } from '@/api/config';
import axios from 'axios';

export default function LearningPath({
  onXPEarned,
  journeyMode,
  onJourneyModeChange,
  learningPackData,
  onOpenLearningPack = null,
  onSelectionChange,
  onToggleChatMode,
  onAskBudleeAI,
  refreshKey = 0,
  chatMode = false,
  initialPdfPage = 1,
  citationHighlights = [],
  citationHighlightedHighlightId = null,
  mainContentOverride = null,
  // Mobile split props
  // Mobile split props
  showPdf = true,
  isMobileSplit = false,
  isFullScreenJourney = false,
  onTogglePdf = null,
  onRequestFullJourney = null,
  navigationRequest = null,
  onNavigationRequestHandled = null,
  onSectionScroll = null, // Callback when PDF scrolls to new section
  hideMobileSelectionButton = false,
  onTextbookOpen = null, // Callback when textbook/PDF viewer is opened
  onTextbookClose = null, // Callback when textbook/PDF viewer is closed by user
  shouldShowTextbook = false, // Whether to show textbook on mount (for state restoration)
  // Journey index management
  currentJourneyIndex = 0,
  onJourneyIndexChange = null,
  isPanelExpanded = false,
}) {
  // Use context for selection
  const {
    selectedSubject,
    selectedChapter,
    selectedSubjectId,
    selectedChapterId,
    updateSubject,
    updateChapter,
    updateSelection
  } = useSubjectChapter();

  const [selectedLesson, setSelectedLesson] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showTextbook, _setShowTextbook] = useState(false);
  const [currentTextbookLesson, setCurrentTextbookLesson] = useState(null);
  const showTextbookRef = useRef(false);

  const setShowTextbook = useCallback((value) => {
    showTextbookRef.current = value;
    _setShowTextbook(value);
  }, []);
  const [targetSection, setTargetSection] = useState(null);
  const [targetAnnotationId, setTargetAnnotationId] = useState(null);
  const [activeAnnotationId, setActiveAnnotationId] = useState(null); // For visual focus/blink
  const [floatingXP, setFloatingXP] = useState(null);
  const [showBasicConcepts, setShowBasicConcepts] = useState(false);
  const [isSelectionExpanded, setIsSelectionExpanded] = useState(false); // Mobile selection panel state

  // ... (keep state)

  // ... (keep state)

  const [chapterSections, setChapterSections] = useState([]);
  const [chapterAnnotations, setChapterAnnotations] = useState([]);

  // Backend data
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [chaptersData, setChaptersData] = useState({});
  const [loadingSubjects, setLoadingSubjects] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSelectionAlert, setShowSelectionAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertDescription, setAlertDescription] = useState('');

  // Learning pack state - uses chapter ID to load correct progress from localStorage
  const packState = useLearningPack(selectedChapterId ? `chapter_${selectedChapterId}` : null);

  // Fetch user profile from backend if board/grade is missing
  const fetchUserProfile = async (portal) => {
    try {
      const token = getAuthToken(portal);
      if (!token) {
        console.warn('No auth token found');
        return null;
      }

      const response = await axios.get(apiUrl('users/profile/'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Update localStorage with fresh data
      // Note: Profile endpoint may not return board, so preserve it from existing userData
      const userData = getUserData(portal) || {};
      const updatedUserData = {
        ...userData,
        ...response.data,
        // Preserve board from existing data if profile doesn't return it
        board: response.data.board || userData.board,
        grade: response.data.grade || userData.grade,
      };

      // If still no board, try to get it from school's primary_board via login endpoint
      // This is a fallback - ideally the profile endpoint should return board
      if (!updatedUserData.board && updatedUserData.school_code) {
        console.warn('Board not found in profile, may need to re-login to get board data');
      }

      const refreshToken = localStorage.getItem(`${portal}_refresh_token`) || '';
      setAuthTokens(portal, token, refreshToken, updatedUserData);

      return updatedUserData;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  };

  const handleNavigateToAnnotation = (annotationId) => {
    console.log('📍 Navigating to annotation:', annotationId);

    // Ensure we are viewing the textbook
    if (selectedChapterId && chaptersData[selectedChapterId]) {
      // If textbook is not open, open it
      if (!showTextbook || !currentTextbookLesson) {
        const lessonWithPdf = buildLessonFromChapter(selectedChapterId);
        setCurrentTextbookLesson(lessonWithPdf);
        setShowTextbook(true);
      }

      // Close learning pack card view if open
      setIsLearningPackOpen(false);

      // Set target annotation ID to trigger scroll
      setTargetAnnotationId(annotationId);

      // Clear target section to avoid conflicts
      setTargetSection(null);
    } else {
      setAlertMessage('Chapter Not Scanned');
      setAlertDescription('The chapter content is not fully loaded. Please try refreshing.');
      setShowSelectionAlert(true);
    }
  };

  const handleSelectionChange = (selection, chapterId = null) => {
    // If user changes selection while a learning pack is open, close it to prevent mismatched activities.
    if (isLearningPackOpen) {
      setIsLearningPackOpen(false);
      setLearningPackActivities([]);
    }

    // Reset targets when selection changes
    setTargetAnnotationId(null);
    setTargetSection(null);

    // Update selection in context
    if (selection && selection.subject && selection.chapter) {
      // When both subject and chapter are present, use the new subject id if available
      updateSelection(selection.subjectId, selection.subject, chapterId, selection.chapter);
    } else if (selection && selection.subject) {
      // Use the new subject id if provided in selection
      updateSubject(selection.subjectId, selection.subject);
    } else if (chapterId) {
      updateChapter(chapterId, selection.chapter ?? '');
    }
  };

  // Fetch subjects and chapters from backend
  useEffect(() => {
    const loadSubjectsAndChapters = async () => {
      try {
        setErrorMessage('');
        const portal = getPortalType(window.location.pathname || '');
        let userData = getUserData(portal);

        // If userData is missing or doesn't have board/grade, try to fetch from backend
        if (!userData || !userData.board || !userData.grade) {
          console.log('User data missing board/grade, fetching from backend...');
          userData = await fetchUserProfile(portal);
        }

        if (!userData) {
          console.error('Unable to get user data');
          setErrorMessage('Unable to load user information. Please try logging in again.');
          setLoadingSubjects(false);
          return;
        }

        if (!userData.board || !userData.grade) {
          console.error('User data missing board or grade:', {
            hasBoard: !!userData.board,
            hasGrade: !!userData.grade,
            userData
          });
          setErrorMessage('Your profile is missing board or grade information. Please contact your administrator.');
          setLoadingSubjects(false);
          return;
        }

        const boardId = userData.board.id;
        const gradeId = userData.grade.id;

        console.log('Fetching subjects for board:', boardId, 'grade:', gradeId);

        // Fetch subjects
        const subjectsList = await subjectsAPI.list({ board: boardId, grade: gradeId });

        if (!subjectsList || subjectsList.length === 0) {
          console.warn('No subjects found for board:', boardId, 'grade:', gradeId);
          setErrorMessage('No subjects available for your board and grade. Please contact your administrator.');
          setSubjects([]);
          setLoadingSubjects(false);
          return;
        }

        const subjectsFormatted = subjectsList.map(s => ({
          value: s.id.toString(),
          label: s.subject_name,
          id: s.id
        }));

        console.log('Loaded subjects:', subjectsFormatted);
        setSubjects(subjectsFormatted);
        setLoadingSubjects(false);
      } catch (error) {
        console.error('Error loading subjects:', error);
        setErrorMessage(
          error.response?.status === 401
            ? 'Authentication failed. Please log in again.'
            : error.response?.status === 403
              ? 'You do not have permission to access this content.'
              : 'Failed to load subjects. Please try again later.'
        );
        setLoadingSubjects(false);
      }
    };

    loadSubjectsAndChapters();
  }, []);

  // Fetch chapters when subject is selected
  useEffect(() => {
    const loadChapters = async () => {
      if (!selectedSubjectId) {
        setChapters([]);
        setChaptersData({});
        return;
      }

      try {
        const chaptersList = await chaptersAPI.list({ subject: selectedSubjectId });
        const chaptersFormatted = chaptersList.map(c => ({
          value: c.id.toString(),
          label: c.chapter_name,
          id: c.id,
          file_url: c.file_url,
          file_upload: c.file_upload
        }));
        setChapters(chaptersFormatted);

        // Store full chapter data for PDF access
        const chaptersMap = {};
        chaptersList.forEach(c => {
          chaptersMap[c.id] = c;
        });
        setChaptersData(chaptersMap);
      } catch (error) {
        console.error('Error loading chapters:', error);
        setChapters([]);
      }
    };

    loadChapters();
  }, [selectedSubjectId]);

  // Load cached PDF sections + chapter annotations for building the section path
  useEffect(() => {
    const loadChapterData = async () => {
      if (!selectedChapterId) {
        setChapterSections([]);
        setChapterAnnotations([]);
        return;
      }

      try {
        const portal = getPortalType(window.location.pathname || '');
        const authToken = getAuthToken(portal);
        if (!authToken) {
          setChapterSections([]);
          setChapterAnnotations([]);
          return;
        }

        // Sections (cached in sessionStorage to avoid regenerating signed S3 URLs on feature switches)
        // Signed URLs are time-limited; keep this cache shorter than the server-side expiry.
        // CACHE VERSION: Increment this when backend section logic changes to force refresh
        const CACHE_VERSION = '1.1';  // Updated to force refresh after annotation-based section addition
        const sectionsCacheKey = `chapter_sections_cache_v${CACHE_VERSION}:${selectedChapterId}`;
        const now = Date.now();
        const maxAgeMs = 45 * 60 * 1000; // 45 minutes

        const maybeUseCachedSections = () => {
          try {
            const raw = sessionStorage.getItem(sectionsCacheKey);
            if (!raw) return false;
            const cached = JSON.parse(raw);
            const age = now - (cached?.at || 0);
            if (age < 0 || age > maxAgeMs) return false;
            if (!Array.isArray(cached?.sections)) return false;
            setChapterSections(cached.sections);

            // Prefetch icons so switching views feels instant.
            const iconUrls = cached.sections
              .map((sec) => String(sec?.icon_url ?? sec?.iconUrl ?? '').trim())
              .filter(Boolean);
            iconUrls.forEach((url) => {
              const img = new Image();
              img.src = url;
            });

            return true;
          } catch {
            return false;
          }
        };

        const usedCache = maybeUseCachedSections();

        const refreshSectionsFromServer = async () => {
          try {
            const sectionsResp = await fetch(apiUrl(`userportal/superadmin/chapters/${selectedChapterId}/sections/`), {
              headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json',
              },
              cache: 'default',
            });
            if (sectionsResp.ok) {
              const data = await sectionsResp.json();
              const sections = Array.isArray(data?.sections) ? data.sections : [];
              console.log(`📋 Loaded ${sections.length} sections for chapter ${selectedChapterId}:`, sections.map(s => `${s.sectionNumber || s.section_number}: ${s.title || s.section_name}`));
              setChapterSections(sections);

              try {
                sessionStorage.setItem(
                  sectionsCacheKey,
                  JSON.stringify({ at: Date.now(), sections })
                );
              } catch {
                // Ignore storage quota / privacy mode failures
              }

              const iconUrls = sections
                .map((sec) => String(sec?.icon_url ?? sec?.iconUrl ?? '').trim())
                .filter(Boolean);
              iconUrls.forEach((url) => {
                const img = new Image();
                img.src = url;
              });
              return;
            }

            // If server fetch fails and we didn't have cache, fall back to empty.
            if (!usedCache) setChapterSections([]);
          } catch {
            if (!usedCache) setChapterSections([]);
          }
        };
        // Always refresh in background so icon updates (including custom section icons)
        // reflect quickly without waiting for the sessionStorage TTL.
        refreshSectionsFromServer();

        // Annotations (activities)
        try {
          const annResp = await axios.get(apiUrl('superadmin/chapter-annotations/'), {
            headers: {
              'Authorization': `Bearer ${authToken}`,
              'Content-Type': 'application/json',
            },
            params: { chapter: selectedChapterId },
          });
          const payload = annResp?.data;
          const list = Array.isArray(payload)
            ? payload
            : (Array.isArray(payload?.results) ? payload.results : []);
          console.log(`📝 Loaded ${list.length} annotations for chapter ${selectedChapterId}`);
          const sectionCounts = {};
          list.forEach(ann => {
            const sn = ann.section_number || 'no-section';
            sectionCounts[sn] = (sectionCounts[sn] || 0) + 1;
          });
          console.log(`📝 Annotations by section:`, sectionCounts);
          setChapterAnnotations(list);
        } catch {
          setChapterAnnotations([]);
        }
      } catch {
        setChapterSections([]);
        setChapterAnnotations([]);
      }
    };

    loadChapterData();
  }, [selectedChapterId, refreshKey]);

  const isActivityAnnotation = useMemo(() => {
    const allowed = new Set([
      'watch',
      'video',
      'explore',
      'practice',
      'curiosity',
      'basicconcepts',
      'basic concepts',
    ]);
    return (ann) => {
      const raw = String(ann?.annotation_type ?? '').trim().toLowerCase();
      return raw && allowed.has(raw);
    };
  }, []);

  // Aligned with PDFSectionViewer and ActivityProgressList grouping logic
  const sectionNodes = useMemo(() => {
    // 1. Filter relevant activities
    const activities = (Array.isArray(chapterAnnotations) ? chapterAnnotations : []).filter(isActivityAnnotation);
    if (activities.length === 0) return [];

    // 2. Prepare Section Lookup Map
    const sectionByNumber = new Map();
    (Array.isArray(chapterSections) ? chapterSections : []).forEach((sec) => {
      const sn = String(sec?.sectionNumber ?? sec?.section_number ?? '').trim();
      if (sn) sectionByNumber.set(sn, sec);
    });

    // 3. Grouping Helper (Strict Name > Number priority)
    // 3. Grouping Helper (Strict Name > Number priority)
    // Build a lookup for section names from the authoritative `sections` metadata
    // This allows us to group "9.2" (unnamed activity) with "Thrust" (named activity)
    const sectionNameByNumber = new Map();
    (Array.isArray(chapterSections) ? chapterSections : []).forEach((sec) => {
      const sn = String(sec?.sectionNumber ?? sec?.section_number ?? '').trim();
      const title = String(sec?.title ?? sec?.fullTitle ?? '').trim();
      if (sn && title) sectionNameByNumber.set(sn, title);
    });

    console.log(`🗺️ Section metadata lookup map:`, Object.fromEntries(sectionNameByNumber));

    const getGroupKey = (act) => {
      const rawName = act?.section_name ?? act?.sectionName ?? '';
      const name = typeof rawName === 'string' ? rawName.trim() : '';
      const sn = String(act.section_number ?? act.sectionNumber ?? '').trim();

      if (sn) {
        // STRICTER LOGIC:
        // If we have a valid section number that maps to a known section title, USE THAT TITLE.
        const metadataName = sectionNameByNumber.get(sn);
        if (metadataName) return `name:${metadataName}`;

        if (name) return `name:${name}`;
        return `num:${sn}`;
      }

      if (name) return `name:${name}`;
      return 'other';
    };

    // 4. Group Activities
    const groups = new Map();
    for (const act of activities) {
      const key = getGroupKey(act);
      if (key === 'other') continue;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(act);
    }

    console.log(`🎯 Grouped ${activities.length} activities into ${groups.size} section groups:`,
      Array.from(groups.entries()).map(([key, acts]) => `${key} (${acts.length} activities)`)
    );

    // 5. Build Nodes from Groups
    const out = [];
    for (const [key, anns] of groups.entries()) {
      // Determine display title and number
      const first = anns[0];
      const name = (typeof first?.section_name === 'string' ? first.section_name.trim() : '');
      const number = (typeof first?.section_number === 'string' ? first.section_number.trim() : '');

      // Try to match with PDF section metadata
      let sec = null;
      if (number) sec = sectionByNumber.get(number);

      const rawTitle = String(sec?.title ?? sec?.fullTitle ?? '').trim();

      // Prefer annotation one_liner or title if available
      let displayTitle = '';
      if (first?.one_liner) {
        displayTitle = first.one_liner;
      } else if (first?.title) {
        displayTitle = first.title;
      } else {
        displayTitle = name || rawTitle || number || 'Section';
      }

      const iconUrl = String(sec?.icon_url ?? sec?.iconUrl ?? '').trim() || null;

      // Determine Page Number (Critical for sorting)
      const minPage = Math.min(...anns.map(a => parseInt(a?.page_number, 10) || 999999));
      const startPage = parseInt(sec?.pageNumber ?? sec?.page_number, 10) || (minPage !== 999999 ? minPage : 1);

      // Section Number for display/key
      const displaySectionNumber = number || (sec?.sectionNumber) || '';

      out.push({
        id: `section-${key}`,
        type: 'chapter-section',
        status: 'available', // Will be computed below
        position: 'center',
        title: displayTitle,
        fullTitle: displayTitle,
        showTitle: true,
        iconUrl,
        fullData: {
          sectionNumber: displaySectionNumber,
          pageNumber: startPage,
          targetSection: displayTitle, // For PDF navigation
          groupKey: key // Store for debugging/reference
        },
        activities: anns // Keep track of activities in this node
      });
    }

    // 6. Sort Nodes (Page > Number > Title)
    out.sort((a, b) => {
      const pa = a.fullData.pageNumber;
      const pb = b.fullData.pageNumber;
      if (pa !== pb) return pa - pb;

      // Secondary: Section Number (Numeric-aware)
      const sna = a.fullData.sectionNumber;
      const snb = b.fullData.sectionNumber;
      if (sna && snb) {
        return sna.localeCompare(snb, undefined, { numeric: true, sensitivity: 'base' });
      }
      return 0;
    });

    // 7. Compute Status (Sequential)
    let hasFoundActive = false;
    const nodesWithStatus = out.map((node, idx) => {
      const nodeActivities = node.activities || [];

      // Check completion - sections with no activities are considered completed (headers/checkpoints)
      let isCompleted = false;
      if (nodeActivities.length === 0) {
        isCompleted = true; // Empty sections (headers) are auto-completed
      } else {
        isCompleted = nodeActivities.every(act => {
          const actId = String(act.annotation_id || act.id);
          const completed = packState.isItemCompleted(actId);
          console.log(`🔍 [Completion Check] Activity:`, {
            actId,
            annotation_id: act.annotation_id,
            id: act.id,
            type: act.annotation_type,
            completed,
            activityObject: act
          });
          return completed;
        });
      }

      let status = 'available';

      if (isCompleted) {
        status = 'completed';
      } else if (!hasFoundActive) {
        status = 'active';
        hasFoundActive = true;
        console.log(`✅ [Status] Node ${idx} "${node.title}" is ACTIVE (first incomplete)`);
      } else {
        status = 'available';
      }

      console.log(`📊 [Status] Node ${idx} "${node.title}": ${nodeActivities.length} activities, completed: ${isCompleted}, status: ${status}`);

      return { ...node, status };
    });

    // Debug: Log how many active nodes we have
    const activeCount = nodesWithStatus.filter(n => n.status === 'active').length;
    console.log(`🎯 [LearningPath] Active nodes count: ${activeCount} (should be 1)`);

    return nodesWithStatus;

  }, [chapterAnnotations, chapterSections, isActivityAnnotation, packState]);





  const handleActivityComplete = (itemId, xpReward) => {
    const earnedXP = packState.completeItem(itemId, xpReward);

    if (earnedXP > 0) {
      // Show floating XP text
      setFloatingXP({ amount: earnedXP, timestamp: Date.now() });
      setTimeout(() => setFloatingXP(null), 1000);

      // Show toast notification
      toast.success(`🎉 +${earnedXP} Gems earned!`, {
        duration: 1000
      });

      // Notify parent to update gems counter
      if (onXPEarned) {
        onXPEarned(earnedXP);
      }
    }
  };

  const handleSubjectChange = (value) => {
    const subject = subjects.find(s => s.id.toString() === value);
    updateSubject(subject?.id || null, subject?.label || '');
    updateChapter(null, '');
    updateSubject(subject?.id || null, subject?.label || '');
    updateChapter(null, '');
    setShowTextbook(false); // Reset textbook view when subject changes

    // Notify parent of selection change
    if (onSelectionChange) {
      onSelectionChange({ subject: subject?.label || '', subjectId: subject?.id || null, chapter: '' }, null);
      onSelectionChange({ subject: subject?.label || '', subjectId: subject?.id || null, chapter: '' }, null);
    }
    if (onJourneyModeChange) {
      onJourneyModeChange(false);
    }

    // Minimize selection panel on mobile after selection change
    setIsSelectionExpanded(false);
  };

  const buildLessonFromChapter = (chapterId) => {
    if (!chapterId || !chaptersData[chapterId]) return null;
    const chapterData = chaptersData[chapterId];

    // Check if PDF exists
    const hasPdf = !!(chapterData.file_url || chapterData.file_upload || chapterData.is_ready);
    const pdfUrl = hasPdf ? apiUrl(`superadmin/chapters/${chapterId}/pdf/`) : null;

    return {
      title: chapterData.chapter_name,
      fullTitle: chapterData.chapter_name,
      pdfUrl: pdfUrl,
      textbookImage: null
    };
  };

  const handleChapterChange = (value) => {
    const chapter = chapters.find(c => c.id.toString() === value);
    const newChapterId = chapter?.id || null;
    const wasViewingTextbook = showTextbook && currentTextbookLesson;

    // If chapter is actually changing, reset the viewer state
    if (newChapterId !== selectedChapterId) {
      // Close textbook viewer if open
      setShowTextbook(false);
      setCurrentTextbookLesson(null);

      // Reset lesson selection
      setSelectedLesson(null);
      setIsModalOpen(false);
    }

    updateChapter(newChapterId, chapter?.label || '');

    // OPTIMIZATION: Prefetch PDF when chapter is selected (before user clicks)
    // This makes PDF loading instant when user opens it
    if (newChapterId && chaptersData[newChapterId]) {
      const chapterData = chaptersData[newChapterId];
      const hasPdf = !!(chapterData.file_url || chapterData.file_upload || chapterData.is_ready);

      if (hasPdf) {
        const pdfUrl = apiUrl(`superadmin/chapters/${newChapterId}/pdf/`);
        const portal = getPortalType(window.location.pathname || '');
        const token = getAuthToken(portal);

        // Prefetch PDF in background (browser will cache it)
        if (token) {
          fetch(pdfUrl, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
            cache: 'default', // Browser will cache based on Cache-Control headers
          }).catch(() => {
            // Silently fail - prefetch is optional optimization
          });
        }
      }
    }

    // If user was viewing textbook and new chapter is selected, auto-load new chapter's PDF
    if (wasViewingTextbook && newChapterId && chaptersData[newChapterId]) {
      const lessonWithPdf = buildLessonFromChapter(newChapterId);
      // Small delay to ensure state is updated
      setTimeout(() => {
        setCurrentTextbookLesson(lessonWithPdf);
        setShowTextbook(true);
      }, 100);
    }

    // Notify parent of selection change
    if (onSelectionChange) {
      onSelectionChange({ subject: selectedSubject, subjectId: selectedSubjectId, chapter: chapter?.label || '' }, newChapterId);
      onSelectionChange({ subject: selectedSubject, subjectId: selectedSubjectId, chapter: chapter?.label || '' }, newChapterId);
    }

    // Minimize selection panel on mobile after selection change
    setIsSelectionExpanded(false);
  };

  const currentChapters = chapters;

  // Handle navigation requests from parent
  useEffect(() => {
    if (navigationRequest && navigationRequest.type === 'annotation' && navigationRequest.id) {
      console.log('📍 Handling navigation request:', navigationRequest);

      if (selectedChapterId && chaptersData[selectedChapterId]) {
        // Open textbook if not already open
        if (!showTextbook || !currentTextbookLesson) {
          const lessonWithPdf = buildLessonFromChapter(selectedChapterId);
          setCurrentTextbookLesson(lessonWithPdf);
          setShowTextbook(true);
        }

        // Note: Parent component (LearnPage) handles closing the learning pack overlay

        setTargetAnnotationId(navigationRequest.id);
        setTargetSection(null);

        if (onNavigationRequestHandled) {
          onNavigationRequestHandled();
        }
      }
    }
  }, [navigationRequest, selectedChapterId, chaptersData, showTextbook, currentTextbookLesson, onNavigationRequestHandled]);

  // Notify parent when textbook is opened/closed
  useEffect(() => {
    if (onTextbookOpen) {
      onTextbookOpen(showTextbook);
    }
  }, [showTextbook, onTextbookOpen]);

  // Restore textbook state when shouldShowTextbook becomes true (e.g., after component re-mount)
  useLayoutEffect(() => {
    if (shouldShowTextbook && !showTextbook) {
      setShowTextbook(true);
      if (selectedChapterId && chaptersData[selectedChapterId]) {
        const lessonWithPdf = buildLessonFromChapter(selectedChapterId);
        if (lessonWithPdf) {
          setCurrentTextbookLesson(lessonWithPdf);
        }
      }
    }
  }, [shouldShowTextbook, showTextbook, selectedChapterId, chaptersData]);

  const handleStartLesson = (lesson) => {
    // Validate that subject and chapter are selected
    if (!selectedSubjectId || !selectedChapterId) {
      setAlertMessage('Please Select Subject and Chapter');
      setAlertDescription('Use the dropdowns at the top to select your subject and chapter before starting the lesson.');
      setShowSelectionAlert(true);
      setIsModalOpen(false);
      return;
    }

    // If we have a selected chapter from backend, use its PDF URL
    if (selectedChapterId && chaptersData[selectedChapterId]) {
      const chapterData = chaptersData[selectedChapterId];
      console.log('📄 Chapter data:', {
        chapterId: selectedChapterId,
        chapterName: chapterData.chapter_name,
        file_url: chapterData.file_url,
        file_upload: chapterData.file_upload,
        hasFileUrl: !!chapterData.file_url,
        hasFileUpload: !!chapterData.file_upload,
      });

      // Check if PDF exists
      const hasPdf = !!(chapterData.file_url || chapterData.file_upload || chapterData.is_ready);

      // Always use the backend API endpoint for PDF access (handles authentication)
      // The endpoint will serve the PDF from S3/R2
      // Only set URL if PDF actually exists
      const pdfUrl = hasPdf ? apiUrl(`superadmin/chapters/${selectedChapterId}/pdf/`) : null;

      console.log('📄 Constructed PDF URL:', pdfUrl);

      const lessonWithPdf = {
        ...lesson,
        pdfUrl: pdfUrl,
        title: chapterData.chapter_name,
        fullTitle: chapterData.chapter_name,
        // Remove textbookImage when PDF is available to ensure PDF viewer is shown
        textbookImage: null
      };

      console.log('📄 Setting lesson with PDF:', lessonWithPdf);
      setCurrentTextbookLesson(lessonWithPdf);
      setShowTextbook(true);
      try {
        trackContentEvent('content_play', {
          content_id: `chapter-${selectedChapterId}`,
          content_type: 'pdf',
          content_title: chapterData.chapter_name,
          subject_id: selectedSubjectId || null,
          chapter_id: selectedChapterId || null,
        });
      } catch (e) { }
    } else {
      console.log('⚠️ No chapter selected or chapter data missing');
      setAlertMessage('Chapter Not Found');
      setAlertDescription('Chapter data not found. Please select a valid chapter from the dropdown.');
      setShowSelectionAlert(true);
    }
    setIsModalOpen(false);
  };

  const handleCloseTextbook = () => {
    setShowTextbook(false);
    setCurrentTextbookLesson(null);
    setTargetSection(null);
    if (onTextbookClose) {
      onTextbookClose();
    }
  };

  // Generate nodes based on selected chapter
  const getNodes = () => {
    const nodes = [
      { id: 'welcome', type: 'start', status: 'active', position: 'center', title: 'Welcome Lesson', fullTitle: 'Welcome Lesson', showTitle: true },
    ];

    // Section path: only sections that actually contain activities
    sectionNodes.forEach((n) => nodes.push(n));

    return nodes;
  };



  const handleNodeClick = (node) => {
    // START node - open textbook directly (no modal)
    if (node.id === 'welcome') {
      // Validate that subject and chapter are selected
      if (!selectedSubjectId || !selectedChapterId) {
        setAlertMessage('Please Select Subject and Chapter');
        setAlertDescription('Use the dropdowns at the top to select your subject and chapter before clicking START.');
        setShowSelectionAlert(true);
        return;
      }

      // If we have a selected chapter from backend, use its PDF URL
      if (selectedChapterId && chaptersData[selectedChapterId]) {
        const chapterData = chaptersData[selectedChapterId];
        console.log('📄 Welcome Lesson - Chapter data:', {
          chapterId: selectedChapterId,
          chapterName: chapterData.chapter_name,
          file_url: chapterData.file_url,
          file_upload: chapterData.file_upload,
        });

        // Check if PDF exists
        const hasPdf = !!(chapterData.file_url || chapterData.file_upload || chapterData.is_ready);

        // Always use the backend API endpoint for PDF access (handles authentication)
        // The endpoint will serve the PDF from S3/R2
        // Only set URL if PDF actually exists
        const pdfUrl = hasPdf ? apiUrl(`superadmin/chapters/${selectedChapterId}/pdf/`) : null;

        console.log('📄 Welcome Lesson - Constructed PDF URL:', pdfUrl);

        const lessonWithPdf = {
          ...node,
          pdfUrl: pdfUrl,
          title: chapterData.chapter_name,
          fullTitle: chapterData.chapter_name,
          // Remove textbookImage when PDF is available to ensure PDF viewer is shown
          textbookImage: null
        };

        console.log('📄 Welcome Lesson - Setting lesson with PDF:', lessonWithPdf);
        setCurrentTextbookLesson(lessonWithPdf);
        setShowTextbook(true);
        setTargetSection(null);
      } else {
        console.log('⚠️ Welcome Lesson - No chapter selected or chapter data missing');
        setAlertMessage('Chapter Not Found');
        setAlertDescription('Chapter data not found. Please select a valid chapter from the dropdown.');
        setShowSelectionAlert(true);
      }
      return;
    }

    // Basic Concepts node - open Basic Concepts interface
    if (node.id === 'basics') {
      // Validate that subject and chapter are selected
      if (!selectedSubjectId || !selectedChapterId) {
        setAlertMessage('Please Select Subject and Chapter');
        setAlertDescription('Use the dropdowns at the top to select your subject and chapter before exploring Basic Concepts.');
        setShowSelectionAlert(true);
        return;
      }

      // Open Basic Concepts interface
      setShowBasicConcepts(true);
      return;
    }

    // Chapter subsection nodes
    if (node?.type === 'chapter-section' && node.fullData) {
      // Open PDF and scroll to section
      if (!selectedSubjectId || !selectedChapterId) {
        setAlertMessage('Please Select Subject and Chapter');
        setAlertDescription('Use the dropdowns at the top to select your subject and chapter before accessing this section.');
        setShowSelectionAlert(true);
        return;
      }

      if (selectedChapterId && chaptersData[selectedChapterId]) {
        const chapterData = chaptersData[selectedChapterId];

        // Check if PDF exists
        const hasPdf = !!(chapterData.file_url || chapterData.file_upload || chapterData.is_ready);

        // Always use the backend API endpoint for PDF access
        // Only set URL if PDF actually exists
        const pdfUrl = hasPdf ? apiUrl(`superadmin/chapters/${selectedChapterId}/pdf/`) : null;

        const lessonWithPdf = {
          ...node,
          pdfUrl: pdfUrl,
          title: chapterData.chapter_name,
          fullTitle: chapterData.chapter_name,
          textbookImage: null
        };

        // Get first annotation ID for precise scrolling
        const firstAnnotation = node.activities && node.activities.length > 0 ? node.activities[0] : null;
        const firstAnnotationId = firstAnnotation?.id || firstAnnotation?.activity_id || null;

        console.log('📍 Section Click - Node data:', {
          nodeId: node.id,
          nodeType: node.type,
          targetSection: node.fullData.targetSection,
          pageNumber: node.fullData.pageNumber,
          sectionNumber: node.fullData.sectionNumber,
          activitiesCount: node.activities?.length || 0,
          firstAnnotationId
        });

        setCurrentTextbookLesson(lessonWithPdf);
        setShowTextbook(true);

        // Use annotation ID for precise scrolling if available, otherwise fall back to section name
        if (firstAnnotationId) {
          setTargetAnnotationId(firstAnnotationId); // Precise scroll to annotation
          setActiveAnnotationId(firstAnnotationId); // Visual focus/blink
          setTargetSection(null); // Clear any previous targetSection
        } else {
          setTargetSection(node.fullData.targetSection); // Fall back to section-based scroll
          setTargetAnnotationId(null); // Clear any previous targetAnnotationId
          setActiveAnnotationId(null); // Clear any previous activeAnnotationId
        }
      } else {
        setAlertMessage('Chapter Not Found');
        setAlertDescription('Chapter data not found. Please select a valid chapter from the dropdown.');
        setShowSelectionAlert(true);
      }
      return;
    }

    if (node.fullData) {
      setSelectedLesson(node.fullData);
      setIsModalOpen(true);
    } else {
      // Other generic nodes - show toast
      if (node.status === 'locked') {
        toast.info('Complete previous lessons to unlock!');
      } else if (node.status === 'active') {
        toast.success('Starting lesson...');
      } else if (node.status === 'completed') {
        toast.success('Lesson already completed!');
      } else {
        toast.info('Lesson available - click to start!');
      }
    }
  };

  // Use sectionNodes which has proper completion tracking
  const nodes = sectionNodes;

  // Determine if we should show compact view
  // Keep consistent when rendering an inline main-content override (e.g., Learning Pack viewer).
  const isCompactMode = journeyMode || showTextbook || !!mainContentOverride;

  // Responsive helper to reduce horizontal offsets on narrow screens to avoid clipping
  const isNarrowScreen = typeof window !== 'undefined' && window.innerWidth < 640;



  return (
    <div
      className={
        'relative h-full min-h-0 flex flex-col bg-[hsl(var(--main-bg))] overflow-hidden'
      }
    >
      {/* Subject & Chapter Selection */}
      <>
        {/* Mobile Minimized Button - Show when both subject & chapter selected and not expanded */}
        {selectedSubjectId && selectedChapterId && !isSelectionExpanded && !hideMobileSelectionButton && (
          <div className="lg:hidden fixed top-4 right-4 z-40">
            <button
              onClick={() => setIsSelectionExpanded(true)}
              className="bg-gradient-to-br from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] text-white rounded-full p-2.5 sm:p-3 shadow-lg hover:shadow-xl transition-all hover:scale-105"
              title="Expand selection panel"
            >
              <ChevronDown className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>
        )}

        {/* Full Selection Panel - Desktop and expanded mobile */}
        {(isSelectionExpanded || typeof window !== 'undefined' && window.innerWidth >= 1024) && (
          <div className={`${isSelectionExpanded ? 'fixed inset-0 z-50 bg-black/50 lg:bg-transparent lg:static lg:z-auto' : ''} flex items-center justify-center lg:relative`}>
            <div className={`mx-auto w-full px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8 ${isCompactMode ? 'mb-3 sm:mb-4 max-w-2xl sm:max-w-3xl' : 'mb-4 sm:mb-6 max-w-4xl lg:max-w-6xl'} ${isSelectionExpanded ? 'lg:bg-transparent lg:pt-0 lg:px-0 lg:mb-0' : ''}`}>
              <Card className={`bg-gradient-to-br from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] border-0 shadow-2xl ${isCompactMode ? 'p-2 sm:p-3' : 'p-4 sm:p-6'}`}>
                <div className={isCompactMode ? 'flex items-center gap-2 sm:gap-3' : 'space-y-3 sm:space-y-4'}>
                  {!isCompactMode && (
                    <div className="flex items-center justify-between w-full">
                      <h2 className="text-lg sm:text-xl font-bold text-[hsl(var(--main-bg))] flex-1">
                        Select Your Learning Focus
                      </h2>
                      {isSelectionExpanded && (
                        <button
                          onClick={() => setIsSelectionExpanded(false)}
                          className="lg:hidden text-[hsl(var(--main-bg))] hover:bg-white/20 p-1.5 rounded transition"
                          title="Close selection panel"
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  )}

                  {errorMessage && (
                    <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg">
                      <p className="text-sm text-red-200 font-medium">{errorMessage}</p>
                    </div>
                  )}

                  <div className={`flex items-center ${isCompactMode ? 'gap-2 sm:gap-3 flex-1' : 'gap-3 sm:gap-4'}`}>
                    <div className="flex-1">
                      <Select
                        value={selectedSubjectId ? selectedSubjectId.toString() : ''}
                        onValueChange={handleSubjectChange}
                      >
                        <SelectTrigger className={`bg-[hsl(var(--main-bg))]/90 border-[hsl(var(--main-bg))]/30 text-white hover:bg-[hsl(var(--main-bg))] font-semibold ${isCompactMode ? 'text-xs sm:text-sm h-8 sm:h-9' : 'text-sm sm:text-base h-9 sm:h-12'}`}>
                          <SelectValue placeholder={loadingSubjects ? "Loading subjects..." : "Subject"} />
                        </SelectTrigger>
                        <SelectContent className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
                          {subjects.map((subject) => (
                            <SelectItem
                              key={subject.id}
                              value={subject.id.toString()}
                              className="text-gray-200 hover:bg-[hsl(var(--sidebar-hover))] hover:text-white cursor-pointer font-medium focus:bg-[hsl(var(--sidebar-hover))] focus:text-white"
                            >
                              {subject.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex-1">
                      <Select
                        value={selectedChapterId ? selectedChapterId.toString() : ''}
                        onValueChange={handleChapterChange}
                        disabled={!selectedSubjectId}
                      >
                        <SelectTrigger className={`bg-[hsl(var(--main-bg))]/90 border-[hsl(var(--main-bg))]/30 text-white hover:bg-[hsl(var(--main-bg))] font-semibold ${isCompactMode ? 'text-xs sm:text-sm h-8 sm:h-9' : 'text-sm sm:text-base h-9 sm:h-12'}`}>
                          <SelectValue placeholder="Chapter" />
                        </SelectTrigger>
                        <SelectContent className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
                          {currentChapters.length > 0 ? (
                            currentChapters.map((chapter) => (
                              <SelectItem
                                key={chapter.id}
                                value={chapter.id.toString()}
                                className="text-gray-200 hover:bg-[hsl(var(--sidebar-hover))] hover:text-white cursor-pointer font-medium focus:bg-[hsl(var(--sidebar-hover))] focus:text-white"
                              >
                                {chapter.label}
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem value="no-chapters" disabled className="text-gray-400 text-center justify-center opacity-100 cursor-default">
                              No chapters available for this subject yet
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    {isCompactMode && isSelectionExpanded && (
                      <button
                        onClick={() => setIsSelectionExpanded(false)}
                        className="lg:hidden text-[hsl(var(--main-bg))] hover:bg-white/20 p-1.5 rounded transition flex-shrink-0"
                        title="Close selection panel"
                      >
                        <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </Card>
            </div>
          </div>
        )}
      </>
      )}

      {/* Floating XP Notification */}
      {floatingXP && (
        <div className="fixed top-20 sm:top-24 right-1/2 transform translate-x-1/2 z-50 animate-float-up">
          <div className="bg-gradient-to-r from-yellow-400 to-amber-500 text-white font-bold text-xl sm:text-2xl px-4 sm:px-6 py-2 sm:py-3 rounded-full shadow-2xl">
            +{floatingXP.amount} XP
          </div>
        </div>
      )}

      {/* Conditional Rendering: Journey Mode OR Textbook OR Learning Path */}
      <div className="flex-1 min-h-0 flex flex-col relative w-full">
        {mainContentOverride ? (
          mainContentOverride
        ) : journeyMode && learningPackData ? (
          <CardJourneyView
            items={learningPackData.items || []}
            onExit={() => onJourneyModeChange(false)}
            onComplete={handleActivityComplete}
            isItemCompleted={packState.isItemCompleted}
            currentIndex={currentJourneyIndex}
            onIndexChange={onJourneyIndexChange}
            onToggleChatMode={onToggleChatMode}
            chatMode={chatMode}
          />
        ) : (showTextbook && currentTextbookLesson) || shouldShowTextbook ? (
          <div className="h-full min-h-0 flex flex-col overflow-hidden">
            <TextbookViewer
              key={`textbook-${selectedChapterId || 'default'}-${refreshKey}`}
              refreshKey={refreshKey}
              lesson={currentTextbookLesson || (shouldShowTextbook && selectedChapterId && chaptersData[selectedChapterId] ? buildLessonFromChapter(selectedChapterId) : null)}
              onClose={handleCloseTextbook}
              onXPEarned={onXPEarned}
              chapterId={selectedChapterId}
              onAskBudleeAI={onAskBudleeAI}
              onToggleChatMode={onToggleChatMode}
              chatMode={chatMode}
              onStartJourney={() => onJourneyModeChange(true)}
              journeyMode={journeyMode}
              targetSection={targetSection}
              targetAnnotationId={targetAnnotationId}
              activeAnnotationId={activeAnnotationId}
              onClearTargetAnnotation={() => setTargetAnnotationId(null)}
              onClearActiveAnnotation={() => setActiveAnnotationId(null)}
              hideBackButton={false}
              pdfFullBleed={false}
              fillParentHeight={false}
              pdfInitialPage={initialPdfPage}
              citationHighlights={citationHighlights}
              citationHighlightedHighlightId={citationHighlightedHighlightId}
              // Mobile split forwarding
              showPdf={showPdf}
              isMobileSplit={isMobileSplit}
              isFullScreenJourney={isFullScreenJourney}
              onTogglePdf={onTogglePdf}
              onRequestFullJourney={onRequestFullJourney}
              onSectionScroll={onSectionScroll}
            />
          </div>
        ) : (
          /* Gamified Learning Path */
          <div className="flex-1 w-full overflow-y-auto overflow-x-hidden px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8 min-h-0 relative" id="learning-path-scroll-container">
            {/* Parallax Background Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
              {/* Stars Layer 1 - Slow moving, faint */}
              {[...Array(20)].map((_, i) => (
                <div
                  key={`star1-${i}`}
                  className="absolute w-1 h-1 bg-white rounded-full opacity-20 parallax-star-slow"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 200}%`,
                    animationDelay: `${Math.random() * 3}s`
                  }}
                />
              ))}

              {/* Stars Layer 2 - Medium speed, brighter */}
              {[...Array(15)].map((_, i) => (
                <div
                  key={`star2-${i}`}
                  className="absolute w-1.5 h-1.5 bg-teal-400/30 rounded-full opacity-40 parallax-star-medium"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 200}%`,
                    animationDelay: `${Math.random() * 2}s`
                  }}
                />
              ))}

              {/* Planets/Celestial Bodies */}
              <div
                className="absolute w-16 h-16 rounded-full bg-gradient-to-br from-purple-500/10 to-blue-500/10 blur-sm parallax-planet-slow"
                style={{ left: '10%', top: '20%' }}
              />
              <div
                className="absolute w-12 h-12 rounded-full bg-gradient-to-br from-teal-500/10 to-green-500/10 blur-sm parallax-planet-slow"
                style={{ left: '80%', top: '60%' }}
              />
              <div
                className="absolute w-20 h-20 rounded-full bg-gradient-to-br from-indigo-500/10 to-purple-500/10 blur-md parallax-planet-slower"
                style={{ left: '15%', top: '120%' }}
              />
            </div>

            <div className="relative max-w-2xl sm:max-w-3xl mx-auto py-8 sm:py-10 z-10 overflow-x-hidden">
              {/* Dynamic SVG Path */}
              <svg
                className="absolute top-0 left-0 w-full h-full pointer-events-none z-0"
                style={{ overflow: 'visible' }}
              >
                <defs>
                  <linearGradient id="activeGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="hsl(var(--primary))" />
                    <stop offset="100%" stopColor="hsl(var(--purple-accent))" />
                  </linearGradient>
                </defs>
                <g style={{ transform: 'translateX(50%)' }}>
                  {(() => {
                    if (nodes.length <= 1) return null;

                    const X_OFFSET = isNarrowScreen ? 90 : 200;
                    const Y_GAP = 120; // Increased back to 120 for better spacing
                    const Y_START = 72;

                    const segments = [];

                    for (let i = 0; i < nodes.length - 1; i++) {
                      const currPattern = i % 4;
                      const nextPattern = (i + 1) % 4;

                      const getOffset = (p) => {
                        if (p === 1) return X_OFFSET;
                        if (p === 3) return -X_OFFSET;
                        return 0;
                      };

                      const x1 = getOffset(currPattern);
                      const y1 = Y_START + (i * Y_GAP);

                      const x2 = getOffset(nextPattern);
                      const y2 = Y_START + ((i + 1) * Y_GAP);

                      // Control points for smooth curve
                      const c1x = x1;
                      const c1y = y1 + (Y_GAP / 2);
                      const c2x = x2;
                      const c2y = y2 - (Y_GAP / 2);

                      const segmentD = `M ${x1} ${y1} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${x2} ${y2}`;

                      // Determine Segment Status
                      const startNode = nodes[i];
                      const endNode = nodes[i + 1];

                      let variant = 'future';
                      if (startNode.status === 'completed') {
                        if (endNode.status === 'completed') variant = 'completed';
                        else if (endNode.status === 'active') variant = 'active';
                      }

                      segments.push(<path
                        key={`seg-${i}`}
                        d={segmentD}
                        stroke={variant === 'completed' ? 'hsl(var(--teal-vivid))' : variant === 'active' ? 'url(#activeGradient)' : 'hsl(var(--muted-foreground))'}
                        strokeWidth={variant === 'future' ? 4 : 6}
                        strokeOpacity={variant === 'future' ? 0.3 : 1}
                        fill="none"
                        strokeLinecap="round"
                        className={variant === 'active' ? 'animate-dash-flow' : ''}
                      />);
                    }
                    return segments;
                  })()}
                </g>
              </svg>

              {/* Nodes */}
              {nodes.map((node, index) => {
                // Layout Constants (Must match SVG)
                const X_OFFSET = isNarrowScreen ? 90 : 200;
                const Y_GAP = 120; // Matches SVG Y_GAP

                // Calculate node height based on screen size (match LearningNode sizes)
                // Mobile: w-10 h-10 = 40px
                // Tablet: sm:w-14 sm:h-14 = 56px
                // Desktop: lg:w-16 lg:h-16 = 64px
                const nodeHeight = isNarrowScreen ? 40 : 64;

                // Horizontal Position
                // Pattern: Center (0) -> Right (1) -> Center (2) -> Left (3)
                const posPattern = index % 4;
                let xPos = 0;
                if (posPattern === 1) xPos = X_OFFSET;
                else if (posPattern === 3) xPos = -X_OFFSET;

                return (
                  <div
                    key={node.id}
                    className="flex justify-center relative z-10"
                    style={{
                      transform: `translateX(${xPos}px)`,
                      marginBottom: index === nodes.length - 1 ? 0 : `${Y_GAP - nodeHeight}px`
                    }}
                  >
                    <div className="relative">
                      <LearningNode
                        node={node}
                        onClick={() => handleNodeClick(node)}
                        delay={index * 150}
                      />
                    </div>
                  </div>
                );
              })}

              {/* Single Traveling Mascot - Positioned to the LEFT side of active node */}
              {(() => {
                const activeIndex = nodes.findIndex(n => n.status === 'active');
                if (activeIndex === -1) return null;

                const X_OFFSET = isNarrowScreen ? 90 : 200;
                const Y_GAP = 120;
                const NODE_HEIGHT = 64;
                const NODE_WIDTH = 64; // Approximate width of a node (same as height since it's circular)
                const SIDE_SPACING = 80; // Space between mascot and node (left side)

                const posPattern = activeIndex % 4;

                // Position mascot to the LEFT of the node
                let xPos = '50%';
                if (posPattern === 1) {
                  // Node is on the right
                  xPos = `calc(50% + ${X_OFFSET}px - ${SIDE_SPACING}px)`;
                } else if (posPattern === 3) {
                  // Node is on the left
                  xPos = `calc(50% - ${X_OFFSET}px - ${SIDE_SPACING}px)`;
                } else {
                  // Node is centered
                  xPos = `calc(50% - ${SIDE_SPACING}px)`;
                }

                // Calculate Y position to align vertically with the node center
                const yPos = 40 + (activeIndex * Y_GAP) + (NODE_HEIGHT / 2) - 24; // -24 to center mascot (half of 48px)

                console.log(`🦉 [Mascot] Positioned LEFT of node ${activeIndex}: ${nodes[activeIndex]?.title}, yPos: ${yPos}px, pattern: ${posPattern}`);

                return (
                  <div
                    className="absolute w-12 h-12 z-30 animate-bounce-slow drop-shadow-md pointer-events-none transition-all duration-700 ease-in-out"
                    style={{
                      left: xPos,
                      top: `${yPos}px`
                    }}
                  >
                    <img
                      src="/logo.png"
                      alt="Budlee Mascot"
                      className="w-full h-full object-contain mascot-glow"
                    />
                  </div>
                );
              })()}

              {/* Flying Mascot Effect - From Last Completed to Next Pending */}
              {(() => {
                // Find last completed node and next pending/active node
                const completedNodes = nodes.filter(n => n.status === 'completed');
                const pendingNodes = nodes.filter(n => n.status === 'active' || n.status === 'available');

                if (completedNodes.length === 0 || pendingNodes.length === 0) return null;

                const lastCompletedNode = completedNodes[completedNodes.length - 1];
                const nextPendingNode = pendingNodes[0];

                const lastCompletedIndex = nodes.findIndex(n => n.id === lastCompletedNode.id);
                const nextPendingIndex = nodes.findIndex(n => n.id === nextPendingNode.id);

                if (lastCompletedIndex === -1 || nextPendingIndex === -1 || lastCompletedIndex >= nextPendingIndex) return null;

                // Calculate positions for the flying path
                const X_OFFSET = isNarrowScreen ? 90 : 200;
                const Y_GAP = 120;
                const Y_START = 72;

                const getOffset = (pattern) => {
                  if (pattern === 1) return X_OFFSET;
                  if (pattern === 3) return -X_OFFSET;
                  return 0;
                };

                const startPattern = lastCompletedIndex % 4;
                const endPattern = nextPendingIndex % 4;

                const x1 = getOffset(startPattern);
                const y1 = Y_START + (lastCompletedIndex * Y_GAP);
                const x2 = getOffset(endPattern);
                const y2 = Y_START + (nextPendingIndex * Y_GAP);

                // Control points for smooth curve (same as SVG path)
                const c1x = x1;
                const c1y = y1 + (Y_GAP / 2);
                const c2x = x2;
                const c2y = y2 - (Y_GAP / 2);

                const pathD = `M ${x1} ${y1} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${x2} ${y2}`;

                return (
                  <svg
                    className="absolute top-0 left-0 w-full h-full pointer-events-none z-30"
                    style={{ overflow: 'visible' }}
                  >
                    <defs>
                      <path id={`flying-path-${lastCompletedIndex}-${nextPendingIndex}`} d={pathD} />
                    </defs>
                    {/* Animated mascot following the path */}
                    <circle r="24" fill="none" stroke="none">
                      <animateMotion
                        dur="3s"
                        repeatCount="indefinite"
                        rotate="auto"
                      >
                        <mpath href={`#flying-path-${lastCompletedIndex}-${nextPendingIndex}`} />
                      </animateMotion>
                      <image
                        href="/logo.png"
                        width="48"
                        height="48"
                        x="-24"
                        y="-24"
                        className="mascot-glow"
                        style={{
                          filter: 'drop-shadow(0 0 12px rgba(52, 211, 153, 0.6)) drop-shadow(0 0 24px rgba(52, 211, 153, 0.3))'
                        }}
                      />
                    </circle>
                  </svg>
                );
              })()}
            </div>
          </div>
        )}

      </div>

      {/* Mascot - Visible only when chat panel is closed - Clickable */}
      {
        !chatMode && (
          <BudleeAIIcon
            size="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16"
            iconSize="w-6 h-6 sm:w-7 h-7 lg:w-8 h-8"
            position="fixed bottom-20 sm:bottom-6 right-4 sm:right-6 lg:bottom-4 lg:right-6"
            className="z-[100]"
            onClick={onToggleChatMode}
            showBadge={true}
          />
        )
      }

      {/* Lesson Modal */}
      <LessonModal
        lesson={selectedLesson}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedLesson(null);
        }}
        onStartLesson={handleStartLesson}
      />

      {/* Basic Concepts Modal */}
      <BasicConceptsModal
        chapterId={selectedChapterId}
        isOpen={showBasicConcepts}
        onClose={() => setShowBasicConcepts(false)}
      />

      {/* Selection Alert Dialog - Centered */}
      <AlertDialog open={showSelectionAlert} onOpenChange={setShowSelectionAlert}>
        <AlertDialogContent className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))]">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-full bg-yellow-500/20">
                <AlertCircle className="w-6 h-6 text-yellow-500" />
              </div>
              <AlertDialogTitle className="text-xl font-bold text-white">
                {alertMessage}
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-gray-200 text-base pt-2">
              {alertDescription}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => setShowSelectionAlert(false)}
              className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white"
            >
              Got it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div >
  );
}
